// Docs content-as-data registry: the single source of truth that drives the
// sidebar tree, the route list, prev/next, per-page <title>/meta, and the right TOC.
// Appending a page = one object in one category module; appending a release =
// one object in releases.ts. registry.ts itself is not touched in Phase 2.
import type { ComponentType } from 'react';

import { getStarted } from './categories/get-started';
import { concepts } from './categories/concepts';
import { interfaceCategory } from './categories/interface';
import { platforms } from './categories/platforms';
import { guides } from './categories/guides';
import { reference } from './categories/reference';
import { troubleshooting } from './categories/troubleshooting';
import { releaseNotes } from './categories/release-notes';
import { faq } from './categories/faq';

export type TocItem = { id: string; label: string };
export type RelatedLink = { label: string; to: string };

export type DocPage = {
  slug: string;            // path after "/docs/"; '' for the index page
  title: string;           // H1 + sidebar label + <title>
  eyebrow: string;         // category name for the breadcrumb eyebrow
  description: string;     // one-line dek: lede under H1 + <meta name=description>
  toc: TocItem[];          // right-rail sections; MUST match the DocSection ids the Body renders
  Body: ComponentType;     // the page body (default export of the page file)
  related?: RelatedLink[]; // "Related" block; DocPageView also derives prev/next from registry order
};

export type DocCategory = { id: string; label: string; pages: DocPage[] };

export type ReleaseNote = {
  version: string;         // e.g. "1.1.0-beta.4"
  label?: string;          // optional friendly label; TODO(founder) — default shows the raw version
  date: string;            // ship date "YYYY-MM-DD"; if unknown, TODO(founder)
  hook: string;            // one-line "what a tester notices first"
  shipped: boolean;        // private flag — false + TODO(founder) if R2 upload is unconfirmed
  New?: string[];
  Improved?: string[];
  Fixed?: string[];
  actionRequired?: string; // rendered as a warn Callout (e.g. "re-index required for a new model")
};

// Sidebar order = reader journey.
export const DOCS: DocCategory[] = [
  getStarted,
  concepts,
  interfaceCategory,
  platforms,
  guides,
  reference,
  troubleshooting,
  releaseNotes,
  faq,
];

// Flattened, in sidebar order — powers routing + prev/next.
export const ALL_PAGES: DocPage[] = DOCS.flatMap((c) => c.pages);

export function slugFromPath(pathname: string): string {
  return pathname.replace(/^\/docs\/?/, '').replace(/\/+$/, '');
}

export function pageForPath(pathname: string): DocPage | undefined {
  const slug = slugFromPath(pathname);
  return ALL_PAGES.find((p) => p.slug === slug);
}

export function categoryForPage(page: DocPage): DocCategory | undefined {
  return DOCS.find((c) => c.pages.includes(page));
}

export { RELEASES } from './releases';
